const MINIMUM_VISIBLE_SLIDES = 9;
const SWIPE_THRESHOLD = 40;

class GalleryCarousel extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('ready')) {
      return;
    }

    this.track = this.querySelector('[gallery-carousel-track]');

    if (!this.track) {
      return;
    }

    this.originals = Array.from(this.track.children);
    this.count = this.originals.length;

    if (this.count === 0) {
      return;
    }

    this.copies = 1;

    if (this.count > 1) {
      this.buildClones();
    }

    this.base = this.count * Math.floor(this.copies / 2);
    this.index = this.base;

    this.setAttribute('ready', '');
    this.render(true);
    this.updateSelectedState();
    this.setupListeners();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  get selectedIndex() {
    return ((this.index % this.count) + this.count) % this.count;
  }

  get selectedCell() {
    return this.originals[this.selectedIndex];
  }

  canChangeSlide() {
    return this.count > 1;
  }

  select(index, { instant = false } = {}) {
    if (this.count < 2) {
      return;
    }

    this.index = index;
    this.render(instant);
    this.updateSelectedState();

    this.dispatchEvent(
      new CustomEvent('carousel:change', { detail: { index: this.selectedIndex, cell: this.selectedCell } })
    );
  }

  previous({ instant = false } = {}) {
    this.normalize();
    this.select(this.index - 1, { instant });
    this.restartAutoplay();
  }

  next({ instant = false } = {}) {
    this.normalize();
    this.select(this.index + 1, { instant });
    this.restartAutoplay();
  }

  buildClones() {
    this.copies = Math.max(3, Math.ceil(MINIMUM_VISIBLE_SLIDES / this.count));

    if (this.copies % 2 === 0) {
      this.copies += 1;
    }

    const middle = Math.floor(this.copies / 2);
    const fragment = document.createDocumentFragment();

    for (let copy = 0; copy < this.copies; copy++) {
      this.originals.forEach((slide) => {
        if (copy === middle) {
          fragment.appendChild(slide);
          return;
        }

        fragment.appendChild(GalleryCarousel.createClone(slide));
      });
    }

    this.track.appendChild(fragment);
  }

  static createClone(slide) {
    const clone = slide.cloneNode(true);

    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-gallery-carousel-clone', '');
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');

    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
      element.removeAttribute('data-shopify-editor-block');
    });
    clone.querySelectorAll('video, video-media').forEach((element) => element.removeAttribute('autoplay'));

    return clone;
  }

  render(instant) {
    if (instant) {
      this.track.style.transition = 'none';
    }

    this.style.setProperty('--gallery-carousel-index', this.index);

    if (instant) {
      this.track.getBoundingClientRect();
      this.track.style.transition = '';
    }
  }

  updateSelectedState() {
    const selected = this.selectedIndex;

    Array.from(this.track.children).forEach((slide, index) => {
      slide.classList.toggle('is-selected', index % this.count === selected);
    });
  }

  normalize() {
    if (this.index >= this.base + this.count) {
      this.index -= this.count;
      this.render(true);
    } else if (this.index < this.base) {
      this.index += this.count;
      this.render(true);
    }
  }

  setupListeners() {
    if (this.count < 2) {
      return;
    }

    this.tabIndex = 0;

    this.addEventListener('keydown', (event) => {
      if (event.target !== this) {
        return;
      }

      if (event.code === 'ArrowLeft') {
        this.previous();
      } else if (event.code === 'ArrowRight') {
        this.next();
      }
    });

    this.addEventListener('touchstart', (event) => {
      this.touchStartX = event.touches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchmove', (event) => {
      if (Math.abs(event.touches[0].clientX - this.touchStartX) > 10) {
        event.preventDefault();
      }
    }, { passive: false });

    this.addEventListener('pointerdown', (event) => {
      this.pointerStart = event.clientX;
    });

    this.addEventListener('pointercancel', () => {
      this.pointerStart = undefined;
    });

    this.addEventListener('pointerup', (event) => {
      if (this.pointerStart === undefined) {
        return;
      }

      const distance = event.clientX - this.pointerStart;
      this.pointerStart = undefined;

      if (Math.abs(distance) < SWIPE_THRESHOLD) {
        return;
      }

      distance < 0 ? this.next() : this.previous();
    });

    if (this.hasAttribute('pause-on-hover')) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.originals.indexOf(event.target);

        if (index !== -1) {
          this.stopAutoplay();
          this.normalize();
          this.select(this.base + index, { instant: event.detail.load });
        }
      });

      this.addEventListener('shopify:block:deselect', () => this.startAutoplay());
    }
  }

  startAutoplay() {
    this.stopAutoplay();

    const speed = parseFloat(this.getAttribute('autoplay'));

    if (!speed || this.count < 2) {
      return;
    }

    this.autoplayTimer = setInterval(() => {
      this.normalize();
      this.select(this.index + 1);
    }, speed * 1000);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    if (this.autoplayTimer) {
      this.startAutoplay();
    }
  }
}

if (!window.customElements.get('gallery-carousel')) {
  window.customElements.define('gallery-carousel', GalleryCarousel);
}
const removeZoomLockingViewport = (node) => {
  if (node.nodeType === Node.ELEMENT_NODE && node.matches('meta[name="viewport"]') && /user-scalable\s*=\s*(no|0)|maximum-scale\s*=\s*1(\.0+)?\s*(,|$)/i.test(node.content)) {
    node.remove();
  }
};

document.head.querySelectorAll('meta[name="viewport"]').forEach(removeZoomLockingViewport);

new MutationObserver((records) => {
  records.forEach((record) => record.addedNodes.forEach(removeZoomLockingViewport));
}).observe(document.head, { childList: true });
